import {
  EventTextSizes,
  ExperienceColorMode,
  ExperienceVibe,
} from '@/types/gql.generated';
import {
  FONT_CALENDAR_HEADING,
  FONT_HEADING,
  FONT_TEXT,
} from '../printables/css/customFonts';
import type { ColorPalette } from './color.types';
import { LARGE_EVENT_TEXT_SCALE } from './lib/gridEventText.lib';
import {
  type CategoryFill,
  getPaletteLookupColor,
  readableInkOn,
  resolveCategoryFill,
  toPaletteColorName,
} from './lib/paletteLookup';
import { applyResponsiveStyles, isResponsiveMatch } from './lib/size.lib';
import type { EventDisplayVariant, Vibe, VibeStyles } from './vibe.types';

// A list row's category bar, minus its fill. Spans the row's full content height:
// alignSelf stretch stops at the content box, so the row's own vertical padding is
// already what keeps consecutive bars from meeting end-to-end and reading as one
// stripe. It carried a matching margin of its own back when it was absolutely
// positioned off the row's border box and had to reproduce that padding manually —
// as a flex/grid child that inset applied twice. Hoisted so each row's slot call
// allocates only the fill.
const LIST_ACCENT_BAR = {
  alignSelf: 'stretch',
  borderRadius: '0.075em',
  flexShrink: 0,
  w: '0.15em',
} as const;

// A categorized Card's surface fill and the ink its body rides, resolved through
// the shared category resolver — the same one the Style sidebar swatches and the
// Summer vibe use — so the Card follows the Bold/Light toggle: Bold paints the
// category's 500 solid, Light its 100 wash.
//
// The ink is where this vibe departs from Summer. On Light's wash it keeps this
// vibe's own warm ink, the understated pairing Planner has always drawn, rather
// than the resolver's design-system ink; only Bold's solid takes the resolver's
// contrast text, where warm ink would be illegible. The Light test is the same one
// resolveCategoryFill makes internally, so the ink can never disagree with the
// surface it sits on.
//
// Returns null for uncategorized cards and every non-card variant, which keep the
// neutral warm panel and the vibe's neutral ink.
const cardCategoryFill = (
  palette: ColorPalette,
  variant: EventDisplayVariant | undefined,
  colorName: string | null | undefined,
  colorMode: ExperienceColorMode | null | undefined
): CategoryFill | null => {
  if (variant !== 'card' || !colorName) return null;

  const fill = resolveCategoryFill(
    toPaletteColorName(colorName),
    palette.id,
    colorMode
  );

  return colorMode === ExperienceColorMode.Light
    ? { ...fill, text: palette.colors.warm['800'] }
    : fill;
};

const style: VibeStyles = {
  AccentDisplay: () => ({}),
  AccentText: ({ palette }) => ({
    color: palette.colors.warm['600'],
    fontFamily: FONT_TEXT,
    fontSize: '0.6em',
    fontWeight: 300,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  }),
  CollectionContainer: () => ({}),
  // Day boxes sit apart rather than sharing gridlines in this vibe, so the gap
  // is what separates one day from the next — roomy enough to read as distinct
  // cards. On paper the month takes a tighter gap than the week: the week spends
  // it across 7 boxes on one row, whereas the month repeats it down 5-6 rows, so
  // the same value reads as too much air between cells. The month grid passes
  // size="sm" and the week passes nothing. The view's layoutId is readable here
  // now, so this could branch on it directly — the size modifier predates that
  // and is kept because it says what the slot actually varies on.
  CollectionGrid: ({ isPrintEnv, size }) => {
    if (isPrintEnv) {
      return { gap: size === 'sm' ? '0.3125em' : '0.5em' };
    }

    return { gap: { base: '0.25em', md: '0.5em' } };
  },
  CollectionGridHeader: ({ palette }) => ({
    color: palette.colors.warm['600'],
    fontFamily: FONT_TEXT,
    fontSize: '0.8em',
    fontWeight: 500,
    textAlign: 'center',
    textTransform: 'uppercase',
  }),
  ColorIndicator: ({ palette, category }) => {
    const neutral = palette.colors.warm;
    const color = category?.colorName
      ? getPaletteLookupColor(palette, category.colorName, 'shade.500')
      : neutral['600'];

    return {
      color,
      fontSize: '0.55em',
    };
  },
  DayContainer: ({ experience, palette, day, size }) => {
    const neutral = palette.colors.warm;
    const hasBlockingEvent = (day?.blockingEvents ?? []).length > 0;

    if (day?.isOutOfBounds) {
      return {
        visibility: 'hidden',
      };
    }

    return {
      // Weekend cells take a subtle warm tint when "Shade weekends" is on; a
      // blocking event still out-reads it with the darker blockout fill.
      bg: hasBlockingEvent
        ? neutral['100']
        : day?.isWeekend && experience.shadeWeekendsEnabled
          ? neutral['50']
          : neutral['0'],
      border: 'solid 1px',
      borderColor: neutral['300'],
      borderRadius: isResponsiveMatch(size, 'lg') ? '0.6em' : '0.25em',
    };
  },
  DayContainerHeader: ({ experience, palette, day, isPrintEnv, size }) => {
    const neutral = palette.colors.warm;
    const hasBlockingEvent = (day?.blockingEvents ?? []).length > 0;
    // Weekend dates render muted only when "Mute weekend dates" is on.
    const muteWeekend = Boolean(
      day?.isWeekend && experience.muteWeekendDatesEnabled
    );

    return applyResponsiveStyles(size, {
      base: {
        color: muteWeekend ? neutral['400'] : neutral['900'],
        fontFamily: FONT_CALENDAR_HEADING,
        fontSize: '1em',
        fontWeight: 600,
        textAlign: 'left',
      },
      md: {
        bg: hasBlockingEvent ? neutral['200'] : neutral['100'],
        borderBottom: 'solid 1px',
        borderColor: neutral['300'],
        // color inherits from base so weekend dates stay muted on the week grid
        // too (base already selects neutral['400'] vs neutral['900'] via muteWeekend).
        fontSize: isPrintEnv ? '1.3em' : { base: '1.15em', md: '1.3em' },
        fontWeight: 300,
      },
    });
  },
  EventCardContainer: ({ palette, isInteractive, eventTextSize }) => {
    const neutral = palette.colors.warm;

    return {
      color: neutral['600'],
      // 'inherit' rather than undefined so it survives the interactive button
      // reset spread, which sets fontSize: 'inherit'.
      fontSize:
        eventTextSize === EventTextSizes.Large
          ? `${LARGE_EVENT_TEXT_SCALE}em`
          : 'inherit',
      letterSpacing: '-0.008em',
      lineHeight: '1.25',
      _hover: isInteractive
        ? {
            borderRadius: 'md',
            transition:
              'box-shadow 0.15s ease, opacity 0.15s ease, transform 0.15s ease',
            _hover: {
              boxShadow: 'md',
              opacity: 0.8,
              transform: 'translateY(-1px)',
            },
          }
        : {},
    };
  },
  // Card display type: a soft warm panel matching how this vibe draws its day
  // boxes. A categorized card carries its category colour so the category still
  // reads on the card, the way it does via the marker/bar/title on the other display
  // types — at the strength the Bold/Light toggle asks for (Light's 100 wash keeps
  // this vibe's understated feel, Bold's 500 solid is the louder card the author
  // opted into). An uncategorized card sits among tinted siblings as a soft neutral
  // panel. The filled cards carry no border — the fill is the shape; only the
  // colors-off case, which has no fill to read against a white cell, outlines
  // instead. Geometry matches Summer's card so the two vibes occupy the same space
  // in a cell. Returns nothing for the other variants so the layout can spread it
  // unconditionally.
  EventCardSurface: ({
    experience,
    palette,
    category,
    variant,
    categoryColorsEnabled = true,
  }) => {
    if (variant !== 'card') return {};
    const neutral = palette.colors.warm;
    const geometry = {
      borderRadius: '0.5em',
      p: '0.4em 0.55em',
    } as const;

    // Color categories off: no fill to carry the shape against a white cell, so
    // outline on white instead — the one case that keeps a border.
    if (!categoryColorsEnabled) {
      return {
        ...geometry,
        bg: neutral['0'],
        border: 'solid 1px',
        borderColor: neutral['300'],
      };
    }

    const fill = cardCategoryFill(
      palette,
      variant,
      category?.colorName,
      experience.colorMode
    );

    return {
      ...geometry,
      bg: fill?.background ?? neutral['100'],
    };
  },
  // Line display type: a left bar carrying the category hue, falling back to a warm
  // rule when uncategorized. The bar is a graphic rather than text, so it takes the
  // full-strength 500.
  EventAccentBar: ({ palette, category, variant }) => {
    if (variant !== 'line') return {};

    return {
      borderLeft: '0.2em solid',
      borderLeftColor: category?.colorName
        ? getPaletteLookupColor(palette, category.colorName, 'shade.500')
        : palette.colors.warm['400'],
      pl: '0.55em',
    };
  },
  // Text carries the category on the title, being its only signal. Card darkens to
  // dark ink — the fill already shows the category — unless it is a Bold solid,
  // which brings its own contrast pairing. Bullet/Line keep EventCardContainer's
  // muted title.
  EventTitle: ({ experience, palette, category, variant }) => {
    const categoryHue = category?.colorName
      ? getPaletteLookupColor(palette, category.colorName, 'shade.500')
      : null;

    if (variant === 'card') {
      const fill = cardCategoryFill(
        palette,
        variant,
        category?.colorName,
        experience.colorMode
      );

      return { color: fill?.text ?? palette.colors.warm['800'] };
    }

    // The other variants sit straight on the day cell.
    const cell = palette.colors.warm['0'];

    if (variant !== 'text') {
      return {};
    }

    return {
      color: categoryHue
        ? readableInkOn(categoryHue, cell)
        : palette.colors.warm['900'],
      fontWeight: 400,
    };
  },
  EventLocationText: ({ experience, palette, category, variant }) => {
    const neutral = palette.colors.warm;
    const fill = cardCategoryFill(
      palette,
      variant,
      category?.colorName,
      experience.colorMode
    );

    return {
      // The Card matches its title/time against the category fill. Other display types
      // sit straight on the day cell, where 600 is the lightest legible rung: 400 read
      // 1.73:1 plain and 1.49:1 on a blocked day. 600 clears 4.5:1 on all three fills.
      color: fill?.text ?? neutral[variant === 'card' ? '800' : '600'],
      fontSize: '0.9em',
    };
  },
  // The timing label is metadata, not a category signal — it stays a quiet neutral
  // whatever the category. This vibe carries the hue in the leading marker (Bullet)
  // and the accent bar (Line) instead, so tinting the time as well spent the color
  // twice on the variants that already show it.
  EventTimeText: ({ experience, palette, category, variant }) => {
    const fill = cardCategoryFill(
      palette,
      variant,
      category?.colorName,
      experience.colorMode
    );

    return {
      // Darker on the Card so the metadata reads on the category fill, in step with
      // the title and location — or the fill's own contrast text on a Bold solid;
      // other display types keep the quiet neutral.
      color:
        fill?.text ?? palette.colors.warm[variant === 'card' ? '800' : '600'],
      fontWeight: 500,
    };
  },
  Legend: ({ palette, isMobile, isPrintEnv }) => ({
    color: palette.colors.warm['600'],
    fontFamily: FONT_TEXT,
    fontSize: isMobile && !isPrintEnv ? '0.85em' : '0.75em',
    letterSpacing: '0.025em',
  }),
  // A blocked-day swatch: its fill and outline. Must agree with DayContainer's
  // blocked-day branch above — the same warm 100 wash, outlined in the same warm
  // 300 the cells rule themselves with. Both read from the palette, so a cool
  // accent's swatch cools with the grid.
  LegendBlockIndicator: ({ palette }) => ({
    color: palette.colors.warm['100'],
    borderColor: palette.colors.warm['300'],
  }),
  // Printable List section heading ("February 2026"). This vibe spends no accent
  // anywhere — its whole language is warm neutrals — so the heading is dark ink in
  // the calendar display face at the same light weight the day-box headers take,
  // over a hairline warm rule rather than a tinted one.
  ListGroupHeader: ({ palette }) => {
    const neutral = palette.colors.warm;

    return {
      borderBottom: 'solid 1px',
      borderBottomColor: neutral['300'],
      color: neutral['900'],
      fontFamily: FONT_CALENDAR_HEADING,
      fontSize: '1.3em',
      fontWeight: 300,
      textTransform: 'none',
    };
  },
  // Printable List row date ("Feb 1"): the calendar face in dark ink, matching how
  // this vibe inks the day numbers in its grids. The weekday beneath it stays a
  // small neutral label owned by the component.
  ListEventDate: ({ palette }) => ({
    color: palette.colors.warm['900'],
    fontFamily: FONT_CALENDAR_HEADING,
    fontSize: '1.05em',
    fontWeight: 600,
    textTransform: 'none',
  }),
  // The ink a list row's secondary text (location and time) inherits. The
  // description sets its own, one step lighter, via ListEventDescription.
  ListRow: ({ palette }) => ({
    color: palette.colors.warm['700'],
  }),
  // Full-strength 500 like the grid's accent bar — a graphic, not text, so the hue is
  // undarkened. Narrower than the grid's 0.2em, which on a row this wide reads as a
  // slab rather than a marker.
  ListRowAccentBar: ({ palette, category, categoryColorsEnabled = true }) => {
    if (!categoryColorsEnabled || !category?.colorName) return {};

    return {
      ...LIST_ACCENT_BAR,
      bg: getPaletteLookupColor(palette, category.colorName, 'shade.500'),
    };
  },
  ListEventTitle: ({ palette }) => ({
    color: palette.colors.warm['900'],
  }),
  // The row's time takes this vibe's calendar display face, pairing it with the
  // date badge across the row; its color stays the row's inherited secondary ink.
  ListEventTime: () => ({
    fontFamily: FONT_CALENDAR_HEADING,
  }),
  // One warm step lighter than the row's secondary ink, so a long description
  // settles behind the title and location instead of reading at their weight.
  ListEventDescription: ({ palette }) => ({
    color: palette.colors.warm['500'],
  }),
  // The weekday micro-label under the date. Deliberately not the lightest step:
  // it renders around 0.6em, where too little contrast stops being quiet and
  // starts being unreadable in print.
  ListEventWeekday: ({ palette }) => ({
    color: palette.colors.warm['600'],
  }),
  // The sheet's headline takes the same calendar display face as the group
  // headers below it, in the same dark warm ink — this vibe spends no accent, so
  // scale alone carries the hierarchy.
  ListHeaderTitle: ({ palette }) => ({
    color: palette.colors.warm['900'],
    fontFamily: FONT_CALENDAR_HEADING,
    fontWeight: 400,
    letterSpacing: 'normal',
    textTransform: 'none',
  }),
  // The date range is a subtitle, so it drops to the body face and a lighter ink
  // rather than competing with the title's display face.
  ListHeaderDateRange: ({ palette }) => ({
    color: palette.colors.warm['600'],
    fontFamily: FONT_TEXT,
    fontWeight: 400,
    letterSpacing: 'normal',
    textTransform: 'none',
  }),
  // No rule under the list header — this vibe spends no accent, and the title's scale
  // plus the group headers' hairlines already carry the structure.
  ListHeaderRule: () => ({ display: 'none' }),
  // The list card is lifted with the same shadow the interactive month/week
  // grid card uses. Overrides the boldness-driven card shadow.
  ListCardSurface: () => ({
    boxShadow: 'ah-card-raised',
  }),
  // Summer makes the card's date row its one accent-colored line. This vibe spends
  // no accent, so the date takes the same quiet warm neutral it gives event times
  // on the plain (non-card) display types — enough to sit below the title without
  // borrowing a colour the vibe doesn't use. Without this slot the row fell through
  // to the app design system's inherited ink.
  ListCardDate: ({ palette }) => ({
    color: palette.colors.warm['600'],
  }),
  ListDateBadge: ({ palette }) => ({
    color: palette.colors.warm['900'],
  }),
  // The month box matches this vibe's day boxes: white on a hairline warm-gray
  // rule, with the same generous corner the week boxes take.
  MonthCardContainer: ({ palette }) => {
    const neutral = palette.colors.warm;

    return {
      bg: neutral['0'],
      border: 'solid 1px',
      borderColor: neutral['300'],
      borderRadius: '0.6em',
    };
  },
  // The label band follows the Bold/Light toggle the way a Card's surface does. Light
  // mirrors DayContainerHeader's tinted strip; Bold inverts it to a warm 800 solid
  // under the paper neutral — this vibe spends no accent, so the neutral ramp is the
  // only place to find the louder fill Bold's 500 solid gives an event card. Only fill,
  // ink and the hairline change, and Bold drops the hairline: a warm 300 rule reads as
  // a seam against a band that already separates itself from the box.
  MonthCardHeader: ({ experience, palette }) => {
    const neutral = palette.colors.warm;
    const type = {
      fontFamily: FONT_CALENDAR_HEADING,
      fontSize: '1.05em',
      fontWeight: 300,
      textTransform: 'none',
    } as const;

    if (experience.colorMode === ExperienceColorMode.Bold) {
      return {
        ...type,
        bg: neutral['800'],
        color: neutral['0'],
      };
    }

    return {
      ...type,
      bg: neutral['100'],
      borderBottom: 'solid 1px',
      borderBottomColor: neutral['300'],
      color: neutral['900'],
    };
  },
  // Gentler than the grid's 1.22em: a month-card box is one of three columns
  // holding a dense list of dated rows, so the grid's step overflows it. Tunable.
  MonthCardEventContainer: ({ eventTextSize }) => ({
    fontSize: eventTextSize === EventTextSizes.Large ? '1.1em' : 'inherit',
  }),
  MonthCardEventDate: ({ palette }) => ({
    color: palette.colors.warm['900'],
    fontFamily: FONT_CALENDAR_HEADING,
    fontWeight: 600,
  }),
  // The row's trailing details (weekday, location, time): the same quiet warm
  // neutral this vibe gives its other event metadata, below the darker date.
  MonthCardEventSecondary: ({ palette }) => ({
    color: palette.colors.warm['600'],
  }),
  // Content, not metadata, so it takes the row's darkest warm ink rather than the quiet
  // neutral of the location beside it. Never the category hue: the title already carries
  // it, and twice on one inline line reads as two signals.
  MonthCardEventDescription: ({ palette }) => ({
    color: palette.colors.warm['900'],
  }),
  // The title leads the row, so it takes the same dark warm ink as the date
  // beside it — in the body face, which is what separates the two.
  // Month cards carry the category on the title, the way the Text display type
  // does on a grid — the row has no card or marker to put it on. Darkened where
  // the hue would not read as ink on the card's white box.
  MonthCardEventTitle: ({ palette, category }) => ({
    color: category?.colorName
      ? readableInkOn(
          getPaletteLookupColor(palette, category.colorName, 'shade.500'),
          palette.colors.warm['0']
        )
      : palette.colors.warm['900'],
  }),
  // The page content itself stays white; the canvas behind it is flat neutral.
  Page: ({ palette }) => ({
    bg: palette.colors.warm['0'],
    color: palette.colors.warm['900'],
  }),
  // Overrides the header component's default flex-end: the much shorter title
  // reads better lifted off the baseline. Unconditional — the header wraps on
  // content, so no viewport reliably means "stacked".
  PageHeaderContainer: ({ palette }) => ({
    alignItems: 'center',
    borderBottomStyle: 'solid',
    borderBottomWidth: '0.125em',
    borderColor: palette.colors.warm['400'],
    color: palette.colors.warm['900'],
  }),
  PageHeaderTitle: ({ palette }) => ({
    color: palette.colors.warm['600'],
    fontFamily: FONT_TEXT,
    fontSize: '0.8em',
    fontWeight: 300,
    letterSpacing: '0.1em',
    textOverflow: 'ellipsis',
    textTransform: 'uppercase',
  }),
  PageHeaderSecondary: ({ isPrintEnv }) => {
    // Regression - we don't entirely account for whether items are stacked
    // vertically or per-layout title sizes (recommendation: layout wraps title
    // in a span)
    return {
      fontFamily: FONT_CALENDAR_HEADING,
      fontSize: isPrintEnv ? '2em' : { base: '1.5em', md: '2em' },
      fontWeight: 700,
    };
  },
  PageNumberPrimary: () => ({
    fontFamily: FONT_HEADING,
    fontSize: '1.25em',
    fontWeight: 400,
  }),
};

export const plannerVibe: Vibe = {
  id: ExperienceVibe.Planner,
  meta: {
    fontFamily: 'CooperBT, system-ui, -apple-system, sans-serif',
    fontWeight: 400,
    name: 'Planner',
  },
  supportedDisplaySettings: {
    // No slot in this vibe reads experience.primaryColor — the sheet is built
    // from the palette's warm ramp and the per-event category colors.
    accentColor: false,
    datesInPrimaryColor: false,
    highlightToday: false,
  },
  style,
};
